import { Copy, Check, ExternalLink } from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';

interface QuoteData {
  text: string;
  original_text?: string;
  respondent_index: number;
  respondent_metadata?: Record<string, string>;
  question_text?: string;
  question_id?: string;
  conversation_id?: string;
  message_id?: string;
  media?: {
    /** Pre-signed S3 URL ready for playback */
    url?: string;
    /** S3 file_id (signed URL not yet generated) */
    file_id?: string;
    type: 'audio' | 'video' | 'image';
  };
}

interface ArtifactQuoteCardProps {
  title: string;
  data: { quotes: QuoteData[] };
}

function formatMetadata(meta?: Record<string, string>): string {
  if (!meta) return '';
  const parts: string[] = [];
  if (meta.gender) parts.push(meta.gender);
  if (meta.age) parts.push(meta.age);
  if (meta.segment) parts.push(meta.segment);
  if (parts.length === 0) {
    return Object.values(meta).slice(0, 2).join(', ');
  }
  return parts.join(', ');
}

/**
 * TELV1-357 #3 — the component wraps every quote in &ldquo;…&rdquo;
 * curly marks. When the LLM also wraps the quote text it produces in
 * straight or curly quotes (which it sometimes does), the user sees
 * a doubled opening like `""The main reasons hindering me…"`. Strip
 * one leading and one trailing matched-quote pair from the LLM's
 * payload so the visual quotes come from exactly one source — the
 * component's curly wrappers.
 */
function stripWrappingQuotes(s: string): string {
  if (!s) return s;
  const QUOTE_CHARS = new Set(['"', '“', '”']);
  let out = s.trim();
  while (out.length >= 2) {
    const first = out[0];
    const last = out[out.length - 1];
    if (QUOTE_CHARS.has(first) && QUOTE_CHARS.has(last)) {
      out = out.slice(1, -1).trim();
    } else {
      break;
    }
  }
  return out;
}

function ArtifactQuoteCard({ title, data }: ArtifactQuoteCardProps) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the pending "checkmark" timer on unmount to prevent setState
  // firing on an unmounted component (e.g., when the artifact panel closes
  // within the 2s window).
  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const handleCopyQuote = useCallback((quote: QuoteData, idx: number) => {
    const meta = formatMetadata(quote.respondent_metadata);
    // TELV1-357 #3 — strip any wrapping quote chars in the payload so the
    // clipboard text isn't `""…""` when the LLM also wrapped its quote.
    const text = `"${stripWrappingQuotes(quote.text)}" — Respondent ${quote.respondent_index}${meta ? `, ${meta}` : ''}`;
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopiedIdx(null), 2000);
  }, []);

  if (!data?.quotes?.length) return null;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
      {data.quotes.map((quote, idx) => {
        const meta = formatMetadata(quote.respondent_metadata);
        const hasTranscript = !!quote.conversation_id;
        const mediaUrl = quote.media?.url;
        const mediaType = quote.media?.type;
        // Stable key combining conversation_id + respondent_index + idx
        // (idx fallback handles the rare case where multiple quotes from
        // the same respondent appear in the same artifact).
        const quoteKey = `${quote.conversation_id || 'no-conv'}-${quote.respondent_index ?? 'no-idx'}-${idx}`;

        return (
          <div key={quoteKey} className="bg-white border border-neutral-200 rounded-lg p-4">
            {/* Header: respondent info */}
            <div className="flex items-center justify-between mb-2">
              {hasTranscript ? (
                <button
                  type="button"
                  onClick={() =>
                    openTranscriptPanel(quote.conversation_id!, quote.message_id, quote.question_id)
                  }
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary hover:underline"
                  aria-label={`View transcript for Respondent ${quote.respondent_index}`}
                >
                  <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 bg-chat-primary/15 text-chat-primary rounded-full text-[11px] font-semibold">
                    {quote.respondent_index}
                  </span>
                  {meta && <span className="text-neutral-500 font-normal">{meta}</span>}
                </button>
              ) : (
                <div className="flex items-center gap-1.5 text-xs font-medium text-neutral-500">
                  <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 bg-neutral-100 text-neutral-600 rounded-full text-[11px] font-semibold">
                    {quote.respondent_index}
                  </span>
                  {meta && <span className="text-neutral-500 font-normal">{meta}</span>}
                </div>
              )}
              <button
                type="button"
                onClick={() => handleCopyQuote(quote, idx)}
                className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
                title="Copy with attribution"
                aria-label="Copy quote with attribution"
              >
                {copiedIdx === idx ? (
                  <Check className="h-3 w-3 text-primary" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>

            {/* Question context */}
            {quote.question_text && (
              <p className="text-[11px] text-neutral-400 mb-1.5 italic">
                Re: {quote.question_text}
              </p>
            )}

            {/* Quote text. TELV1-357 #3 — strip a wrapping quote pair from
                the LLM payload so the curly &ldquo;…&rdquo; here is the
                only source of visible quote marks. */}
            <p className="text-sm text-neutral-800 leading-relaxed">
              &ldquo;{stripWrappingQuotes(quote.text)}&rdquo;
            </p>

            {/* Original language */}
            {quote.original_text && (
              <p className="text-sm text-neutral-500 italic mt-1.5 leading-relaxed">
                &ldquo;{stripWrappingQuotes(quote.original_text)}&rdquo;
              </p>
            )}

            {/* Media playback (audio/video) */}
            {mediaUrl && mediaType === 'audio' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                src={mediaUrl}
                controls
                preload="none"
                className="w-full h-8 mt-2"
                aria-label={`Audio response from Respondent ${quote.respondent_index}`}
              />
            )}
            {mediaUrl && mediaType === 'video' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                src={mediaUrl}
                controls
                preload="metadata"
                className="w-full rounded-md mt-2 max-h-64"
                aria-label={`Video response from Respondent ${quote.respondent_index}`}
              />
            )}
            {mediaUrl && mediaType === 'image' && (
              <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="block mt-2">
                <img
                  src={mediaUrl}
                  alt={`Respondent ${quote.respondent_index} submission`}
                  className="w-full rounded-md object-cover border border-neutral-200 hover:opacity-80 transition-opacity cursor-zoom-in"
                />
              </a>
            )}

            {/* View transcript link */}
            {hasTranscript && (
              <button
                type="button"
                onClick={() =>
                  openTranscriptPanel(quote.conversation_id!, quote.message_id, quote.question_id)
                }
                className="flex items-center gap-1 mt-2.5 text-[11px] font-medium text-primary hover:underline"
                aria-label={`Open full transcript for Respondent ${quote.respondent_index}`}
              >
                <ExternalLink className="h-3 w-3" />
                View transcript
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ArtifactQuoteCard;
